import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  SparklesIcon,
  ChartBarIcon,
  CircleStackIcon,
  BoltIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ArrowRightIcon,
  CheckIcon,
  Bars3Icon,
  XMarkIcon,
  BookOpenIcon,
  ClipboardDocumentIcon,
  GiftIcon,
  TagIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import neoshiftLogo from '../images/neoshiftBILogo.png';
import neoshiftLogoWhite from '../images/neoshiftBILogoWhite.png';

const BETA_CODE = 'BETA-4CC6E28F';

export default function Landing() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const copyBetaCode = () => {
    navigator.clipboard.writeText(BETA_CODE);
    setCodeCopied(true);
    toast.success('Beta code copied!');
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const features = [
    {
      icon: ChartBarIcon,
      title: 'Interactive Dashboards',
      description: 'Create stunning, interactive dashboards that bring your data to life with real-time insights.'
    },
    {
      icon: SparklesIcon,
      title: 'AI-Powered Analytics',
      description: 'Leverage AI to automatically discover insights, answer questions, and generate reports.'
    },
    {
      icon: CircleStackIcon,
      title: 'Universal Data Connections',
      description: 'Connect to any data source - databases, spreadsheets, APIs, and more. All in one place.'
    },
    {
      icon: BoltIcon,
      title: 'Lightning Fast',
      description: 'Query millions of rows in seconds with our optimized cloud data warehouse.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption, role-based access, and compliance with industry standards.'
    },
    {
      icon: UserGroupIcon,
      title: 'Team Collaboration',
      description: 'Share insights across your organization and collaborate on data-driven decisions.'
    }
  ];

  const useCases = [
    {
      title: 'Sales Analytics',
      description: 'Track revenue, pipeline, and performance metrics in real-time.',
      stats: ['Revenue Tracking', 'Pipeline Analysis', 'Team Performance']
    },
    {
      title: 'Marketing Intelligence',
      description: 'Measure campaign effectiveness and optimize your marketing spend.',
      stats: ['Campaign ROI', 'Customer Acquisition', 'Channel Performance']
    },
    {
      title: 'Product Analytics',
      description: 'Understand user behavior and drive product improvements.',
      stats: ['User Engagement', 'Feature Adoption', 'Retention Analysis']
    },
    {
      title: 'Financial Reporting',
      description: 'Automate financial reports and gain visibility into business health.',
      stats: ['P&L Statements', 'Cash Flow', 'Budget vs Actuals']
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-surface-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-surface-900/80 backdrop-blur-xl border-b border-surface-200 dark:border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src={neoshiftLogo} 
                alt="Neoshift BI" 
                className="h-8 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/pricing"
                className="text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white transition-colors font-medium"
              >
                Pricing
              </Link>
              <Link
                to="/docs"
                className="flex items-center gap-2 text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white transition-colors font-medium"
              >
                <BookOpenIcon className="w-4 h-4" />
                Docs
              </Link>
              <Link
                to="/login"
                className="text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white transition-colors font-medium"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="btn-primary"
              >
                Join the Beta
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-surface-600 dark:text-surface-400"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-surface-200 dark:border-surface-800">
              <div className="flex flex-col gap-4">
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-left text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white transition-colors font-medium"
                >
                  Pricing
                </Link>
                <Link
                  to="/docs"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-left text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white transition-colors font-medium"
                >
                  <BookOpenIcon className="w-4 h-4" />
                  Docs
                </Link>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-left text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white transition-colors font-medium"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="btn-primary w-full"
                >
                  Join the Beta
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-surface-50 via-white to-primary-50 dark:from-surface-950 dark:via-surface-900 dark:to-primary-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-sm font-semibold mb-8 ring-1 ring-purple-200 dark:ring-purple-800">
              <SparklesIcon className="w-4 h-4" />
              Open Beta &mdash; Limited to 50 Users
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-surface-900 dark:text-white mb-6 leading-tight">
              Be Among the First to
              <span className="block bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">
                Shape the Future of BI
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-surface-600 dark:text-surface-400 mb-10 leading-relaxed max-w-3xl mx-auto">
              Neoshift BI is now in Open Beta. Join as a founding user and get free access to our Basic plan (normally $29/mo), 
              exclusive discounts, and a permanent Founders Badge.
            </p>

            {/* Beta Code Card */}
            <div className="inline-flex flex-col items-center mb-10">
              <p className="text-sm text-surface-500 dark:text-surface-400 mb-2 font-medium">Use this code when you sign up:</p>
              <button
                onClick={copyBetaCode}
                className="group flex items-center gap-3 px-6 py-3 rounded-xl bg-surface-900 dark:bg-white/10 border-2 border-dashed border-primary-400 dark:border-primary-500 hover:border-primary-500 dark:hover:border-primary-400 transition-all"
              >
                <code className="text-2xl font-bold tracking-wider text-primary-500 dark:text-primary-400">{BETA_CODE}</code>
                <ClipboardDocumentIcon className="w-5 h-5 text-surface-400 group-hover:text-primary-400 transition-colors" />
              </button>
              <p className="text-xs text-surface-400 mt-2">{codeCopied ? 'Copied!' : 'Click to copy'}</p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <button
                onClick={() => navigate('/register')}
                className="btn-primary text-lg px-8 py-4 shadow-xl hover:shadow-2xl transition-shadow w-full sm:w-auto"
              >
                Join the Beta
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="btn-secondary text-lg px-8 py-4 w-full sm:w-auto"
              >
                View Pricing
              </button>
            </div>

            {/* Social Proof */}
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-surface-500 dark:text-surface-500">
              <div className="flex items-center gap-2">
                <CheckIcon className="w-5 h-5 text-green-500" />
                <span>Free Basic Plan ($29 value)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckIcon className="w-5 h-5 text-green-500" />
                <span>No Credit Card Required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckIcon className="w-5 h-5 text-green-500" />
                <span>50% Off Upgrades for 1 Year</span>
              </div>
            </div>

            {/* Product Hunt Embed */}
            <div className="mt-10 flex justify-center">
              <div
                style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  border: '1px solid #e0e0e0',
                  borderRadius: 12,
                  padding: 20,
                  maxWidth: 500,
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <img
                    alt="Neoshift BI"
                    src="https://ph-files.imgix.net/107d0421-90eb-4c4a-bc0f-d8b564635d10.png?auto=format&fit=crop&w=80&h=80"
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 8,
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: '1 1 0%', minWidth: 0 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 18,
                        fontWeight: 600,
                        color: '#1a1a1a',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Neoshift BI
                    </h3>
                    <p
                      style={{
                        margin: '4px 0 0',
                        fontSize: 14,
                        color: '#666',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      Turn raw data into AI dashboards in minutes
                    </p>
                  </div>
                </div>
                <a
                  href="https://www.producthunt.com/products/neoshift-bi?embed=true&utm_source=embed&utm_medium=post_embed"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 12,
                    padding: '8px 16px',
                    background: '#ff6154',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  Check it out on Product Hunt →
                </a>
              </div>
            </div>
          </div>

          {/* Hero Video */}
          <div className="mt-16 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-surface-200 dark:border-surface-800 bg-surface-900">
              {/* YouTube Video Embed */}
              <div className="aspect-video">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/0Y_e38oUmW8"
                  title="Neoshift BI Launch Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-72 h-72 bg-primary-500/10 rounded-full blur-3xl -z-10" />
            <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-accent-500/10 rounded-full blur-3xl -z-10" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-surface-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-surface-900 dark:text-white mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-surface-600 dark:text-surface-400 max-w-3xl mx-auto">
              Powerful features that make data analytics accessible to everyone in your organization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-surface-50 dark:bg-surface-800/50 border border-surface-200 dark:border-surface-700 hover:border-primary-400 dark:hover:border-primary-500 transition-all hover:shadow-xl"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-surface-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-surface-600 dark:text-surface-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beta Perks Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-white to-amber-50 dark:from-purple-950/30 dark:via-surface-950 dark:to-amber-950/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-sm font-semibold mb-4">
              <GiftIcon className="w-4 h-4" />
              Beta Exclusive Perks
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-surface-900 dark:text-white mb-4">
              Why Join During Beta?
            </h2>
            <p className="text-xl text-surface-600 dark:text-surface-400 max-w-3xl mx-auto">
              Early users get the best deal. Help us shape the product and lock in exclusive Founders pricing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Perk 1: Free Basic */}
            <div className="relative p-8 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mb-6">
                <TagIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-surface-900 dark:text-white mb-3">
                Free Basic Plan
              </h3>
              <p className="text-surface-600 dark:text-surface-400 leading-relaxed mb-4">
                Sign up during beta and get automatically upgraded from Starter to Basic &mdash; a <strong className="text-surface-900 dark:text-white">$29/mo value, free for your first month</strong>.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-medium">
                  10 GB Storage
                </span>
                <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-medium">
                  3 Dashboards
                </span>
                <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-medium">
                  400 AI Credits/mo
                </span>
              </div>
            </div>

            {/* Perk 2: Upgrade Discounts */}
            <div className="relative p-8 rounded-2xl bg-white dark:bg-surface-900 border-2 border-primary-400 dark:border-primary-500 shadow-xl hover:shadow-2xl transition-shadow">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 bg-gradient-to-r from-primary-500 to-accent-500 text-white text-xs font-bold rounded-full shadow-lg">
                  BEST VALUE
                </span>
              </div>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-400 to-accent-600 flex items-center justify-center mb-6">
                <SparklesIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-surface-900 dark:text-white mb-3">
                Massive Upgrade Discounts
              </h3>
              <p className="text-surface-600 dark:text-surface-400 leading-relaxed mb-4">
                Upgrade during the beta period and get a <strong className="text-surface-900 dark:text-white">huge discount on your first month</strong>, then <strong className="text-surface-900 dark:text-white">50% off every month for your first year</strong>.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20">
                  <span className="text-surface-700 dark:text-surface-300">Pro Plan</span>
                  <span className="font-bold text-primary-600 dark:text-primary-400">50% off for 1 year</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20">
                  <span className="text-surface-700 dark:text-surface-300">Business Plan</span>
                  <span className="font-bold text-primary-600 dark:text-primary-400">50% off for 1 year</span>
                </div>
              </div>
            </div>

            {/* Perk 3: Founders Badge */}
            <div className="relative p-8 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center mb-6">
                <StarIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-surface-900 dark:text-white mb-3">
                Permanent Founders Badge
              </h3>
              <p className="text-surface-600 dark:text-surface-400 leading-relaxed mb-4">
                Get a permanent <strong className="text-surface-900 dark:text-white">Founders Badge</strong> on your profile. Show the world you believed in Neoshift BI from the very beginning.
              </p>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🏆</span>
                </div>
                <div>
                  <p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">Founding Member</p>
                  <p className="text-amber-600 dark:text-amber-400 text-xs">Exclusive &amp; permanent</p>
                </div>
              </div>
            </div>
          </div>

          {/* Beta Code CTA */}
          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-surface-900 dark:bg-white/5 border border-surface-800 dark:border-surface-700">
              <div className="text-left">
                <p className="text-white font-semibold text-lg">Ready to claim your spot?</p>
                <p className="text-surface-400 text-sm">Only available for the first 50 users.</p>
              </div>
              <button
                onClick={copyBetaCode}
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold transition-colors"
              >
                <code className="tracking-wider">{BETA_CODE}</code>
                <ClipboardDocumentIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-6 py-3 rounded-xl bg-white text-surface-900 font-semibold hover:bg-surface-100 transition-colors"
              >
                Sign Up Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-surface-50 to-white dark:from-surface-950 dark:to-surface-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-surface-900 dark:text-white mb-4">
              Built for Every Department
            </h2>
            <p className="text-xl text-surface-600 dark:text-surface-400 max-w-3xl mx-auto">
              From sales to marketing, finance to product - Neoshift BI adapts to your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 hover:shadow-xl transition-shadow"
              >
                <h3 className="text-2xl font-bold text-surface-900 dark:text-white mb-3">
                  {useCase.title}
                </h3>
                <p className="text-surface-600 dark:text-surface-400 mb-6">
                  {useCase.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {useCase.stats.map((stat, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-sm font-medium"
                    >
                      {stat}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-600 to-accent-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
            Don't Miss Your Spot in the Beta
          </h2>
          <p className="text-xl text-primary-100 mb-4">
            Only 50 spots available. Sign up with the beta code below and get instant access 
            to our Basic plan for free &mdash; no credit card required.
          </p>
          <button
            onClick={copyBetaCode}
            className="group inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-white/10 border-2 border-dashed border-white/40 hover:border-white/70 transition-all mb-10"
          >
            <code className="text-2xl font-bold tracking-wider text-white">{BETA_CODE}</code>
            <ClipboardDocumentIcon className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
          </button>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold text-lg hover:bg-primary-50 transition-colors shadow-xl hover:shadow-2xl w-full sm:w-auto"
            >
              Join the Beta
              <ArrowRightIcon className="w-5 h-5 ml-2 inline" />
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-4 bg-primary-700 text-white rounded-xl font-semibold text-lg hover:bg-primary-800 transition-colors w-full sm:w-auto"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-900 border-t border-surface-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="mb-4">
                <img 
                  src={neoshiftLogoWhite} 
                  alt="Neoshift BI" 
                  className="h-8 w-auto mb-4"
                />
              </div>
              <p className="text-surface-400 mb-4">
                The modern business intelligence platform that helps teams make data-driven decisions faster.
              </p>
              <p className="text-surface-500 text-sm">
                © 2026 PT Neo Teknologi Perkasa. All rights reserved.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/pricing"
                    className="text-surface-400 hover:text-white transition-colors"
                  >
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/docs"
                    className="text-surface-400 hover:text-white transition-colors"
                  >
                    Documentation
                  </Link>
                </li>
                <li>
                  <Link
                    to="/register"
                    className="text-surface-400 hover:text-white transition-colors"
                  >
                    Join the Beta
                  </Link>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="text-surface-400 hover:text-white transition-colors"
                  >
                    Sign In
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/terms"
                    className="text-surface-400 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-surface-400 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-surface-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-surface-500 text-sm">
              Made with ❤️ by PT Neo Teknologi Perkasa
            </div>
            <div className="flex items-center gap-6">
              <Link
                to="/terms"
                className="text-surface-500 hover:text-white text-sm transition-colors"
              >
                Terms
              </Link>
              <Link
                to="/privacy"
                className="text-surface-500 hover:text-white text-sm transition-colors"
              >
                Privacy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
